/*
 * Kitsune Performance: homepage behaviour (kp-home.js)
 * 1. Mobile action bar: shown once the hero is out of view (always runs).
 * 2. Scroll reveals on every device: blocks below the fold fade and rise in as
 *    they enter the viewport, items that arrive together are staggered.
 * 3. Desktop pointer effects: a subtle hero parallax, a spotlight that follows
 *    the cursor on feature cards, and mouse dragging of the homepage carousel.
 * Motion (2, 3 and the marquee) is skipped when the visitor prefers reduced
 * motion, and nothing is hidden unless this script runs.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;


  // Mobile action bar: visible after the hero, hidden while typing so it never
  // sits on top of the on-screen keyboard or a focused field.
  const initMobileBar = () => {
    const bar = document.querySelector('[data-kp-mobile-bar]');
    if (!bar) return;
    const hero = document.querySelector('#MainContent .kp-hero, #MainContent .section-slideshow');
    let pastHero = !hero;
    let typing = false;

    const update = () => {
      const show = pastHero && !typing;
      bar.classList.toggle('is-visible', show);
      document.documentElement.classList.toggle('kp-bar-on', show);
    };

    if (hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(hero);
    }

    const isField = (el) => el && el.matches && el.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]');
    document.addEventListener('focusin', (event) => {
      if (isField(event.target)) { typing = true; update(); }
    });
    document.addEventListener('focusout', (event) => {
      if (isField(event.target)) { typing = false; update(); }
    });

    update();
  };

  const REVEAL_SELECTOR = [
    '.title-wrapper',
    '.collapsible-content__title',
    '.section-section-tabs .horizontal-scroll',
    '.grid__item',
    '.image-banner-section',
    '.image-with-text__media-item',
    '.image-with-text__text-item',
    '.accordion',
    '.banner__box',
    '.slider-buttons',
  ].join(',');

  const initReveal = () => {
    const main = document.getElementById('MainContent');
    if (!main || !('IntersectionObserver' in window)) return;

    const settle = (el, order) => {
      el.style.setProperty('--kp-i', order);
      el.classList.add('kp-in');
      // Drop the helper classes once the transition is over so hover effects
      // are not delayed by the stagger.
      window.setTimeout(() => {
        el.classList.remove('kp-r', 'kp-in');
        el.style.removeProperty('--kp-i');
      }, 1300 + order * 70);
    };

    const observer = new IntersectionObserver((entries) => {
      const arriving = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => (a.boundingClientRect.top - b.boundingClientRect.top) || (a.boundingClientRect.left - b.boundingClientRect.left));

      let order = 0;
      arriving.forEach((entry) => {
        const el = entry.target;
        observer.unobserve(el);
        if (el.classList.contains('kp-l')) {
          el.classList.add('kp-in');
          return;
        }
        settle(el, Math.min(order, 5));
        order += 1;
      });
    }, { rootMargin: '0px 0px 0px 0px', threshold: 0 });

    const viewportBottom = window.innerHeight;
    const isBelowFold = (el) => {
      const rect = el.getBoundingClientRect();
      // Hidden tab panels have an empty box: reveal them when they are shown.
      if (rect.width === 0 && rect.height === 0) return true;
      return rect.top > viewportBottom * 0.92;
    };

    main.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
      if (el.closest('.kp-hero') || el.parentElement.closest('.kp-r')) return;
      if (!isBelowFold(el)) return;
      el.classList.add('kp-r');
      observer.observe(el);
    });

    main.querySelectorAll('.shopify-section > .border-top').forEach((el) => {
      if (!isBelowFold(el)) return;
      el.classList.add('kp-l');
      observer.observe(el);
    });

    document.documentElement.classList.add('kp-motion');
  };

  // Hero: move the photo a few pixels against the cursor.
  const initHero = (hero) => {
    const img = hero.querySelector('[data-kp-parallax]') || hero.querySelector('.kp-hero__img');
    if (!img) return;
    let frame = 0;
    let x = 0;
    let y = 0;

    const apply = () => {
      frame = 0;
      img.style.translate = `${(x * -14).toFixed(1)}px ${(y * -10).toFixed(1)}px`;
    };

    hero.addEventListener('pointermove', (event) => {
      const rect = hero.getBoundingClientRect();
      x = (event.clientX - rect.left) / rect.width - 0.5;
      y = (event.clientY - rect.top) / rect.height - 0.5;
      if (!frame) frame = requestAnimationFrame(apply);
    }, { passive: true });

    hero.addEventListener('pointerleave', () => {
      x = 0;
      y = 0;
      if (!frame) frame = requestAnimationFrame(apply);
    }, { passive: true });

    img.style.transition = 'translate 0.8s cubic-bezier(0.16, 1, 0.3, 1)';
  };

  // Hero slides: cross-fade photos (+ promo card) every SLIDE_MS. Pauses on
  // hover, keyboard focus, when the hero is off screen or the tab is hidden,
  // and via the pause button. Reduced motion: no autoplay, manual only.
  const SLIDE_MS = 6500;
  const initSlides = (hero) => {
    const slides = [...hero.querySelectorAll('.kp-hero__slide')];
    if (slides.length < 2) return;
    const overlays = [...hero.querySelectorAll('.kp-hero__overlay')];
    const dots = [...hero.querySelectorAll('[data-kp-goto]')];
    const pauseBtn = hero.querySelector('[data-kp-pause]');
    let index = 0;
    let timer = 0;
    let userPaused = reducedMotion;
    let hovered = false;
    let focused = false;
    let visible = true;

    hero.style.setProperty('--kp-slide-ms', `${SLIDE_MS}ms`);
    hero.classList.add('kp-slides-ready');

    // Later slides are lazy so they never compete with the first photo (LCP);
    // once the page has loaded, fetch them so the first switch is seamless.
    const warm = () => slides.slice(1).forEach((slide) => {
      slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    });
    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true });

    const isPaused = () => userPaused || hovered || focused || !visible || document.hidden;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((el) => el.classList.toggle('is-active', Number(el.dataset.kpSlide) === index));
      overlays.forEach((el) => {
        const on = Number(el.dataset.kpSlide) === index;
        el.classList.toggle('is-active', on);
        el.toggleAttribute('inert', !on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.classList.toggle('is-done', i < index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        // Restart the progress animation on the active dot
        const fill = dot.firstElementChild;
        if (fill && i === index) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      });
    };

    const schedule = () => {
      clearTimeout(timer);
      hero.classList.toggle('is-paused', isPaused());
      if (isPaused()) return;
      timer = setTimeout(() => {
        show(index + 1);
        schedule();
      }, SLIDE_MS);
    };

    // Resuming after a pause restarts the current slide's full interval
    const resume = () => {
      if (!isPaused()) show(index);
      schedule();
    };

    const syncPause = () => {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      pauseBtn.setAttribute('aria-label', userPaused ? pauseBtn.dataset.labelPlay : pauseBtn.dataset.labelPause);
    };

    // Manual navigation stops autoplay, so the slide the visitor chose stays
    // until they resume it with the play button.
    const goManual = (next) => {
      userPaused = true;
      syncPause();
      show(next);
      schedule();
    };

    dots.forEach((dot, i) => dot.addEventListener('click', () => goManual(i)));
    hero.querySelector('[data-kp-prev]')?.addEventListener('click', () => goManual(index - 1));
    hero.querySelector('[data-kp-next]')?.addEventListener('click', () => goManual(index + 1));

    hero.querySelector('[data-kp-slide-controls]')?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goManual(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goManual(index + 1); }
    });

    // Swipe on the photo (phones and tablets)
    const media = hero.querySelector('.kp-hero__media');
    let touchX = null;
    let touchY = 0;
    hero.addEventListener('touchstart', (event) => {
      const t = event.touches[0];
      const r = media ? media.getBoundingClientRect() : null;
      touchX = r && t.clientY >= r.top && t.clientY <= r.bottom ? t.clientX : null;
      touchY = t.clientY;
    }, { passive: true });
    hero.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const t = event.changedTouches[0];
      const dx = t.clientX - touchX;
      const dy = t.clientY - touchY;
      touchX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) goManual(index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    if (pauseBtn) {
      syncPause();
      pauseBtn.addEventListener('click', () => {
        userPaused = !userPaused;
        syncPause();
        resume();
      });
    }

    if (finePointer) {
      hero.addEventListener('pointerenter', () => { hovered = true; schedule(); });
      hero.addEventListener('pointerleave', () => { hovered = false; resume(); });
    }

    hero.addEventListener('focusin', () => { focused = true; schedule(); });
    hero.addEventListener('focusout', (event) => {
      if (hero.contains(event.relatedTarget)) return;
      focused = false;
      resume();
    });

    document.addEventListener('visibilitychange', resume);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        resume();
      }, { threshold: 0.35 }).observe(hero);
    }

    show(0);
    schedule();
  };

  // Feature cards: CSS reads --kp-mx / --kp-my for the radial highlight.
  const initSpotlight = (card) => {
    let frame = 0;
    let mx = '50%';
    let my = '-30%';

    const apply = () => {
      frame = 0;
      card.style.setProperty('--kp-mx', mx);
      card.style.setProperty('--kp-my', my);
    };

    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      mx = `${Math.round(event.clientX - rect.left)}px`;
      my = `${Math.round(event.clientY - rect.top)}px`;
      if (!frame) frame = requestAnimationFrame(apply);
    }, { passive: true });

    card.addEventListener('pointerleave', () => {
      mx = '50%';
      my = '-30%';
      if (!frame) frame = requestAnimationFrame(apply);
    }, { passive: true });
  };

  // Homepage carousel on desktop: drag with the mouse like a swipe. Phones
  // already swipe natively. The slide snaps to the next or previous one after
  // a 40 px drag (looping at both ends), a drag never triggers a click on a
  // slide link, and autoplay restarts from zero after the drag.
  const initCarouselDrag = (component) => {
    const slider = component.querySelector('.slider[data-slide-type="slide"]');
    if (!slider) return;
    const slides = [...slider.querySelectorAll('.slideshow__slide')];
    if (slides.length < 2) return;

    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let moved = false;

    const step = () => slides[1].offsetLeft - slides[0].offsetLeft;

    slider.classList.add('kp-draggable');
    slider.addEventListener('dragstart', (event) => event.preventDefault());

    slider.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = slider.scrollLeft;
    });

    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 6) return;
      if (!moved) {
        moved = true;
        slider.classList.add('kp-dragging');
        window.getSelection()?.removeAllRanges();
        if (component.slider && typeof component.pause === 'function') component.pause();
      }
      slider.scrollLeft = startLeft - dx;
    });

    const end = (event) => {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      const width = step();
      const dx = event.clientX - startX;
      let page = Math.round(startLeft / width);
      if (dx < -40) page += 1;
      else if (dx > 40) page -= 1;
      if (page < 0) page = slides.length - 1;
      if (page > slides.length - 1) page = 0;
      slider.classList.remove('kp-dragging');
      slider.scrollTo({ left: page * width, behavior: 'smooth' });
      const autoplayOn = slider.dataset.autoplay === 'true' && component.autoplayButtonIsSetToPlay !== false;
      if (autoplayOn && !reducedMotion && component.slider && typeof component.play === 'function') component.play();
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);

    // A drag must not open the slide's link or button.
    slider.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
  };

  // Brand marquee: stop the loop while it is off screen to save battery.
  const initMarquee = () => {
    const strips = document.querySelectorAll('[data-kp-marquee]');
    if (!strips.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('is-paused', !entry.isIntersecting));
    });
    strips.forEach((strip) => observer.observe(strip));
  };

  const init = () => {
    initMobileBar();
    document.querySelectorAll('.kp-hero').forEach(initSlides);
    if (finePointer) document.querySelectorAll('#MainContent .section-slideshow slideshow-component').forEach(initCarouselDrag);
    if (reducedMotion) return;
    initReveal();
    initMarquee();
    if (!finePointer) return;
    document.querySelectorAll('.kp-hero').forEach(initHero);
    document
      .querySelectorAll('[id$="__kp_warsztat"] .multicolumn-card, [id$="__kp_porownanie"] .multicolumn-card, [id$="__kp_kroki"] .multicolumn-card')
      .forEach(initSpotlight);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
